import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import '../../styles/Routines.css';

const PERIODICITY_LABEL = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' };

function buildMonthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    opts.push({ value: val, label });
  }
  return opts;
}

function deriveStatus(item) {
  if (item.status) return item.status;
  const pct = item.combined_pct;
  if (pct == null)   return 'not_started';
  if (pct === 100)   return 'completed';
  if (pct === 0)     return 'incomplete';
  return 'pending';
}

function RoutineStatusBadge({ status }) {
  const map = {
    completed:      { label: 'Completed',      cls: 'completed' },
    pending:        { label: 'Pending',        cls: 'pending' },
    incomplete:     { label: 'Incomplete',     cls: 'incomplete' },
    not_started:    { label: 'Not Started',    cls: 'not-started' },
    not_applicable: { label: 'N/A',            cls: 'not-started' },
  };
  const { label, cls } = map[status] || { label: status, cls: 'not-started' };
  return <span className={`rt-status-badge rt-status-${cls}`}>{label}</span>;
}

function AttentionSection({ routineStats }) {
  const attention = routineStats
    .map(item => ({ ...item, _status: deriveStatus(item) }))
    .filter(item => item._status !== 'completed' && item._status !== 'not_applicable')
    .sort((a, b) => {
      const order = { incomplete: 0, pending: 1, not_started: 2 };
      return (order[a._status] ?? 3) - (order[b._status] ?? 3);
    });

  if (attention.length === 0) {
    return (
      <div className="rt-all-done">
        <span className="rt-all-done-icon">✅</span>
        <span>All routines completed for this period!</span>
      </div>
    );
  }

  const byRole = {};
  for (const item of attention) {
    const role = item.role_name || 'General';
    if (!byRole[role]) byRole[role] = [];
    byRole[role].push(item);
  }

  return (
    <div className="rt-attention-section">
      <div className="rt-attention-header">
        <span className="rt-attention-title">
          Needs Attention
          <span className="rt-attention-count">{attention.length}</span>
        </span>
        <span className="rt-attention-sub">Routines that are incomplete or not yet started</span>
      </div>
      {Object.entries(byRole).map(([role, items]) => (
        <div key={role} className="rt-attention-group">
          <div className="rt-attention-role-label">{role}</div>
          {items.map(item => {
            const remaining = item.expected_count != null ? item.expected_count - item.tasks_completed : null;
            return (
              <div key={item.routine.id} className={`rt-attention-item rt-attention-${item._status}`}>
                <div className="rt-attention-left">
                  <span className="rt-attention-name">{item.routine.description}</span>
                  <span className="rt-attention-meta">
                    {item.routine.periodicity}
                    {item.area_name ? ` · ${item.area_name}` : ''}
                    {remaining !== null && remaining > 0 ? ` · ${remaining} more to go` : ''}
                  </span>
                </div>
                <div className="rt-attention-right">
                  {item.expected_count > 0 && (
                    <span className="rt-attention-tasks">{item.tasks_completed}/{item.expected_count}</span>
                  )}
                  <RoutineStatusBadge status={item._status} />
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default function MyRoutines({ embedded = false }) {
  const { appId } = useParams();
  const api = useApi();
  const { searchQuery } = useWorkspace();
  const navigate = useNavigate();
  const monthOptions = useMemo(buildMonthOptions, []);

  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].value);
  const [typeFilter, setTypeFilter]       = useState('all');
  const [data, setData]                   = useState(null);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.getRoutineDashboard(appId, undefined, { mode: 'month', month: selectedMonth })
      .then(setData)
      .catch((err) => setError(err.response?.data?.error || 'Failed to load routines'))
      .finally(() => setLoading(false));
  }, [appId, selectedMonth]);

  const routineStats    = data?.routine_stats || [];
  const totalRoutines   = routineStats.length;
  const completedCount  = routineStats.filter(r => deriveStatus(r) === 'completed').length;
  const pendingCount    = routineStats.filter(r => { const s = deriveStatus(r); return s === 'pending' || s === 'incomplete'; }).length;
  const notStartedCount = routineStats.filter(r => deriveStatus(r) === 'not_started').length;
  const efficiency      = data?.overall_efficiency;

  const presentTypes = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'].filter(p =>
    routineStats.some(r => r.routine?.periodicity === p)
  );

  const filteredStats = routineStats.filter(item => {
    if (typeFilter !== 'all' && item.routine?.periodicity !== typeFilter) return false;
    const sq = (searchQuery || '').toLowerCase().trim();
    if (!sq) return true;
    return [(item.routine?.description || ''), (item.role_name || ''), (item.area_name || ''), (item.routine?.periodicity || '')].some(f => f.toLowerCase().includes(sq));
  });

  return (
    <div className="routine-dashboard">
      {!embedded && (
        <div className="rt-page-header">
          <h1 className="page-title">My Routine Activities</h1>
        </div>
      )}

      {/* Month picker */}
      <div className="rt-topbar">
        <select
          className="rt-filter-select"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
        >
          {monthOptions.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {loading && (
        <div className="rt-loading">
          <span className="rt-spinner" />
          Loading routines…
        </div>
      )}
      {error && <div className="banner banner-error">{error}</div>}

      {!loading && !error && data && (
        <>
          {/* Stats row */}
          <div className="rt-stats-row">
            <div className="rt-stat-card">
              <span className="rt-stat-value">{totalRoutines}</span>
              <span className="rt-stat-label">Total Routines</span>
            </div>
            <div className="rt-stat-card rt-stat-card-green">
              <span className="rt-stat-value">{completedCount}</span>
              <span className="rt-stat-label">Completed</span>
            </div>
            <div className="rt-stat-card rt-stat-card-yellow">
              <span className="rt-stat-value">{pendingCount}</span>
              <span className="rt-stat-label">In Progress / Incomplete</span>
            </div>
            <div className="rt-stat-card rt-stat-card-gray">
              <span className="rt-stat-value">{notStartedCount}</span>
              <span className="rt-stat-label">Not Started</span>
            </div>
            <div className="rt-stat-card rt-stat-card-blue">
              <span className="rt-stat-value">{efficiency != null ? `${efficiency}%` : '–'}</span>
              <span className="rt-stat-label">Overall Efficiency</span>
            </div>
          </div>

          {routineStats.length === 0 ? (
            <div className="rt-empty">
              <div className="rt-empty-icon">📋</div>
              <p className="rt-empty-title">No routines this period</p>
              <p>No routines are defined for your role(s).</p>
              <p className="muted">Ask your admin to add routines under Roles → Routine Activities.</p>
            </div>
          ) : (
            <>
              <AttentionSection routineStats={routineStats} />

              <div className="rt-breakdown-card">
                <div className="rt-breakdown-header">
                  <span className="rt-breakdown-title">All Routines — Breakdown</span>
                  <span className="rt-breakdown-sub">{monthOptions.find(o => o.value === selectedMonth)?.label}</span>
                </div>

                {presentTypes.length > 1 && (
                  <div className="rt-type-filter-tabs">
                    <button className={`rt-type-tab${typeFilter === 'all' ? ' active' : ''}`} onClick={() => setTypeFilter('all')}>All</button>
                    {presentTypes.map(p => (
                      <button key={p} className={`rt-type-tab${typeFilter === p ? ' active' : ''}`} onClick={() => setTypeFilter(p)}>
                        {PERIODICITY_LABEL[p]}
                      </button>
                    ))}
                  </div>
                )}

                <table className="rt-breakdown-table">
                  <thead>
                    <tr>
                      <th style={{ whiteSpace: 'nowrap' }}>Sl No</th>
                      <th>Routine / Activity</th>
                      <th>Role · Area</th>
                      <th>Done / Expected</th>
                      <th>Status</th>
                      <th>Last Completed</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStats.map((item, idx) => {
                      const status = deriveStatus(item);
                      return (
                        <tr key={item.routine.id} className={`rt-breakdown-row rt-row-${status}`}>
                          <td className="rt-serial-cell">{idx + 1}</td>
                          <td>
                            <div className="rt-routine-name">{item.routine.description}</div>
                            <div className="rt-routine-period-tag">{item.routine.periodicity}</div>
                          </td>
                          <td className="rt-area-cell">
                            <div>{item.role_name || '—'}</div>
                            {item.area_name && <div className="rt-area-sub">{item.area_name}</div>}
                          </td>
                          <td className="rt-pct-cell">
                            {item.expected_count != null && item.expected_count > 0 ? (
                              <span className={item.tasks_completed >= item.expected_count ? 'rt-tasks-done' : item.tasks_completed > 0 ? 'rt-tasks-partial' : ''}>
                                {item.tasks_completed}/{item.expected_count}
                              </span>
                            ) : (
                              <span className="rt-no-data">—</span>
                            )}
                          </td>
                          <td>
                            <RoutineStatusBadge status={status} />
                          </td>
                          <td className="rt-date-cell">
                            {item.last_completed_on
                              ? new Date(item.last_completed_on).toLocaleDateString()
                              : '—'}
                          </td>
                          <td>
                            <button
                              className="rt-view-tasks-btn"
                              onClick={() => navigate(`/apps/${appId}/tasks`)}
                            >
                              View Tasks
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
